import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Iter XX.16 — Gully Pass redemption page.
 *
 * URL: /pass/:token
 * Flow:
 *   1. Look up pass status (public — no auth needed).
 *   2. If pass is invalid / redeemed / revoked / expired → show that state.
 *   3. If user is unauthenticated → prompt sign-in with return_to=/pass/:token.
 *   4. If authenticated + active pass → 1-click "Claim Plus" button →
 *      POST /pass/redeem → show success screen.
 */
export default function PassRedemption() {
  const { token: rawToken } = useParams();
  const token = (rawToken || "").trim().toUpperCase();
  const navigate = useNavigate();
  const { user, loading: authLoading, checkAuth } = useAuth();

  const [pass, setPass] = useState(null);
  const [lookupErr, setLookupErr] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);
  const [redeemErr, setRedeemErr] = useState(null);

  // 1) Look up pass status
  useEffect(() => {
    let alive = true;
    async function load() {
      setLookupLoading(true);
      try {
        const { data } = await api.get(`/pass/${encodeURIComponent(token)}`);
        if (alive) setPass(data.pass);
      } catch (e) {
        if (alive) {
          setLookupErr(e?.response?.status === 404 ? "not_found" : "lookup_failed");
        }
      } finally {
        if (alive) setLookupLoading(false);
      }
    }
    if (token) load();
    return () => { alive = false; };
  }, [token]);

  // 2) Actual redemption
  const doRedeem = useCallback(async () => {
    setRedeeming(true);
    setRedeemErr(null);
    try {
      const { data } = await api.post("/pass/redeem", { token });
      setRedeemResult(data);
      // Refresh billing/user info for downstream pages.
      try { await checkAuth(); } catch { /* ignore */ }
    } catch (e) {
      const msg = e?.response?.data?.detail || "redeem_failed";
      setRedeemErr(msg);
    } finally {
      setRedeeming(false);
    }
  }, [token, checkAuth]);

  if (!token) {
    return <Shell><ErrorCard title="Missing pass code" msg="This link doesn't include a pass code." /></Shell>;
  }
  if (lookupLoading || authLoading) {
    return <Shell><div className="text-neutral-500" data-testid="pass-loading">Loading pass…</div></Shell>;
  }
  if (lookupErr === "not_found") {
    return <Shell><ErrorCard title="Pass not found" msg="Double-check the link — this code doesn't exist." /></Shell>;
  }
  if (lookupErr) {
    return <Shell><ErrorCard title="Couldn't load pass" msg="Please try again in a moment." /></Shell>;
  }
  if (!pass) {
    return <Shell><ErrorCard title="Pass not found" msg="Double-check the link — this code doesn't exist." /></Shell>;
  }

  // Redemption complete
  if (redeemResult) {
    return <Shell><SuccessCard result={redeemResult} onContinue={() => navigate("/chat")} /></Shell>;
  }

  // Pass state gates
  if (pass.status === "redeemed") {
    return <Shell><ErrorCard title="Already claimed" msg="This pass has already been used." testid="pass-already-redeemed" /></Shell>;
  }
  if (pass.status === "revoked") {
    return <Shell><ErrorCard title="Pass unavailable" msg="This pass is no longer valid." testid="pass-revoked" /></Shell>;
  }
  if (pass.status === "expired") {
    return <Shell><ErrorCard title="Pass expired" msg="This pass has expired and can no longer be redeemed." testid="pass-expired" /></Shell>;
  }

  // Active — show the claim card
  return (
    <Shell>
      <ActiveCard
        pass={pass}
        user={user}
        redeeming={redeeming}
        redeemErr={redeemErr}
        onRedeem={doRedeem}
      />
    </Shell>
  );
}

// ------------------------------------------------------------------
// Presentational sub-components
// ------------------------------------------------------------------
function Shell({ children }) {
  return (
    <div
      className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-4"
      data-testid="pass-redemption-page"
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="text-2xl font-semibold tracking-tight text-neutral-100">
            Gully
          </Link>
        </div>
        <div className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6 backdrop-blur-sm">
          {children}
        </div>
        <p className="mt-6 text-center text-xs text-neutral-600">
          <Link to="/" className="hover:text-neutral-400">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}

function ActiveCard({ pass, user, redeeming, redeemErr, onRedeem }) {
  const monthsLabel = pass.duration_months === 1
    ? "1 month"
    : `${pass.duration_months} months`;

  if (!user) {
    // Unauthenticated — send them to /login with return_to.
    const returnTo = `/pass/${encodeURIComponent(pass.token)}`;
    const loginHref = `/login?return_to=${encodeURIComponent(returnTo)}`;
    return (
      <div data-testid="pass-login-required">
        <div className="mb-6 text-center">
          <p className="text-4xl mb-3">🎁</p>
          <h1 className="text-2xl font-semibold mb-2">You&apos;ve got a Gully Pass</h1>
          <p className="text-neutral-400 text-sm">
            Sign in to unlock <span className="text-emerald-400 font-medium">{monthsLabel}</span> of Gully Plus.
          </p>
        </div>
        <div className="mb-4 rounded-lg border border-neutral-800 bg-neutral-950 p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Pass code</p>
          <p className="font-mono text-lg tabular-nums text-neutral-100" data-testid="pass-token">
            {pass.token}
          </p>
        </div>
        <Link
          to={loginHref}
          className="block w-full rounded-lg bg-emerald-500 py-3 text-center text-sm font-medium text-neutral-950 hover:bg-emerald-400 transition"
          data-testid="pass-login-cta"
        >
          Sign in to claim →
        </Link>
        <p className="mt-4 text-center text-xs text-neutral-500">
          You&apos;ll come right back here after signing in.
        </p>
      </div>
    );
  }

  // Authenticated — one-click claim
  return (
    <div data-testid="pass-claim-card">
      <div className="mb-6 text-center">
        <p className="text-4xl mb-3">🎁</p>
        <h1 className="text-2xl font-semibold mb-2">Ready to claim</h1>
        <p className="text-neutral-400 text-sm">
          Unlock <span className="text-emerald-400 font-medium">{monthsLabel}</span> of Gully Plus.
        </p>
      </div>
      <div className="mb-4 rounded-lg border border-neutral-800 bg-neutral-950 p-3 text-center">
        <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Pass code</p>
        <p className="font-mono text-lg tabular-nums text-neutral-100" data-testid="pass-token">
          {pass.token}
        </p>
      </div>
      {redeemErr && (
        <div
          className="mb-3 rounded-lg border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300"
          data-testid="pass-redeem-error"
        >
          {friendlyError(redeemErr)}
        </div>
      )}
      <button
        onClick={onRedeem}
        disabled={redeeming}
        className="block w-full rounded-lg bg-emerald-500 py-3 text-center text-sm font-medium text-neutral-950 hover:bg-emerald-400 transition disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="pass-claim-btn"
      >
        {redeeming ? "Claiming…" : "Claim Gully Plus →"}
      </button>
    </div>
  );
}

function SuccessCard({ result, onContinue }) {
  const ends = result?.plus_ends_at ? new Date(result.plus_ends_at) : null;
  const endsStr = ends && !isNaN(ends)
    ? ends.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;
  const monthsLabel = result.duration_months === 1
    ? "1 month"
    : `${result.duration_months} months`;

  return (
    <div className="text-center" data-testid="pass-success">
      <p className="text-4xl mb-3">🏏</p>
      <h1 className="text-2xl font-semibold mb-2">Welcome to Gully Plus</h1>
      <p className="text-neutral-400 text-sm mb-6">
        {result.was_stacked
          ? `We added ${monthsLabel} to your existing Plus.`
          : `Your ${monthsLabel} of Plus starts now.`}
      </p>
      {endsStr && (
        <div className="mb-6 rounded-lg border border-neutral-800 bg-neutral-950 p-3">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Access through</p>
          <p className="text-base text-neutral-100" data-testid="pass-success-ends-at">{endsStr}</p>
        </div>
      )}
      <button
        onClick={onContinue}
        className="block w-full rounded-lg bg-emerald-500 py-3 text-center text-sm font-medium text-neutral-950 hover:bg-emerald-400 transition"
        data-testid="pass-success-continue"
      >
        Start chatting with Gully →
      </button>
    </div>
  );
}

function ErrorCard({ title, msg, testid }) {
  return (
    <div className="text-center" data-testid={testid || "pass-error"}>
      <p className="text-4xl mb-3">⚠️</p>
      <h1 className="text-2xl font-semibold mb-2">{title}</h1>
      <p className="text-neutral-400 text-sm mb-6">{msg}</p>
      <Link
        to="/chat"
        className="block w-full rounded-lg border border-neutral-800 py-3 text-center text-sm font-medium text-neutral-200 hover:bg-neutral-900 transition"
      >
        Continue to Gully
      </Link>
    </div>
  );
}

function friendlyError(code) {
  const map = {
    pass_already_redeemed: "This pass has already been claimed.",
    pass_revoked: "This pass is no longer valid.",
    pass_expired: "This pass has expired.",
    pass_not_found: "We couldn't find that pass.",
  };
  return map[code] || "Something went wrong. Please try again.";
}
